import {
  Box, Button, FormControl, FormHelperText, Grid, InputLabel, Link, Menu,
  MenuItem, OutlinedInput, Paper, Select, SelectChangeEvent, Stack, TextField, Typography,
} from '@mui/material'
import { FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import moment, { Moment } from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReportPeriodContext } from '../../../../../context/report-periods/report-periods';
import { FileUploadHelper } from '../../../miscellaneous-components/file-upload-helper';
import { DialogSaveButtons } from '../../../miscellaneous-components/dialog-save-buttons';
import CustomFieldService from '../../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../../shared/services/custom-field/custom-field.model';
import { EmissionsInputAdvanced } from './emissions-input-advanced-form';
import { ISubsidiary } from '../../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../../shared/utils/subsidiary-utils';
import { EmissionsFilterLogic, IUnitChoice } from '../emissions-filter/emissions-filter-logic';
import { formatTransaction } from '../../../../../shared/utils/format-post-utils';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import { IConversionSource } from '../../../../../shared/services/conversion-source/conversion-source.model';
import { ScreenLabelsContext } from '../../../../../context/screen-labels/screen-labels';
import { IEmission, IEmissionDetail } from '../../../../../shared/services/emission/emission.model';
import { IEmissionCalculation } from '../../../../../shared/services/emission-calculation/emission-calculation.model';
import { isEtsGroupId } from '../../../../../shared/utils/group-ids-utils';
import { resolveEmissionFieldFlags } from '../../../../../shared/utils/emission-field-config';
import ConversionService from '../../../../../shared/services/conversion/conversion.service';
import { IConversion } from '../../../../../shared/services/conversion/conversion.model';

function humanConversionPath(c: IConversion): string {
  const parts = [c.detailLevelOne, c.detailLevelTwo, c.detailLevelThree, c.detailLevelFour, c.detailLevelFive]
    .filter((s): s is string => typeof s === 'string' && Boolean(s.trim()));
  const primary = parts.length > 0 ? parts.join(' · ') : '';
  const scopeSuffix = c.scope ? ` • Scope ${c.scope}` : '';
  return `${primary}${scopeSuffix}` || '—';
}

function formatEmissionMultiplier(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 8 });
  return n.toLocaleString(undefined, { maximumSignificantDigits: 8 });
}

export const EmissionsInputFilter: FunctionComponent<{
  categoryId: string,
  onSubmit: (transaction: any) => void
  groupId: string,
  conversionSources: IConversionSource[],
  emissionFactorSourceHandler: (value: IConversionSource) => void,
  allowMarketBasedEmissionInput?: boolean,
  isSubmitting: boolean,
  subsidiaries: ISubsidiary[],
  selectedEmissionFactorSourceId: string | undefined,
  selectedSubsidiaryId: string,
  reportPeriodId?: string,
  setSelectedSubsidiaryId: React.Dispatch<React.SetStateAction<string | undefined>>,
  emission?: IEmission | null,
  editCalculation?: IEmissionCalculation | null,
  cancelHandler?: () => void,
  /**
   * When true, user must choose a cascade match and click Select to confirm before save (survey emission rules use equivalent UX in-dialog).
   * Manual activity entry defaults to false — save is enabled once `returnId` is resolved.
   */
  requireConversionConfirmation?: boolean,
  /** Certificate ids currently linked to the emission being edited (aggregated across its calcs). */
  initialRenewableEnergyCertificateIds?: string[],
}> = ({
  categoryId,
  onSubmit,
  groupId,
  allowMarketBasedEmissionInput = false,
  isSubmitting,
  subsidiaries,
  selectedEmissionFactorSourceId,
  conversionSources,
  emissionFactorSourceHandler,
  selectedSubsidiaryId,
  setSelectedSubsidiaryId,
  emission,
  reportPeriodId,
  editCalculation,
  cancelHandler,
  requireConversionConfirmation = false,
  initialRenewableEnergyCertificateIds,
}) => {
    //Services
    const customFieldService = new CustomFieldService();
    const reportPeriodContext = useContext(ReportPeriodContext);
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [calendarYear, setCalendarYear] = useState<string>();
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    const fieldFlags = useMemo(
      () => resolveEmissionFieldFlags(userDetails?.organisation?.emissionFieldConfig, customFields ?? []),
      [userDetails?.organisation?.emissionFieldConfig, customFields],
    );
    const [fileData, setFileData] = useState([]);
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    const conversionService = useMemo(() => new ConversionService(), []);
    const [conversionSelectionConfirmed, setConversionSelectionConfirmed] = useState(false);
    const [confirmedConversion, setConfirmedConversion] = useState<IConversion | null>(null);
    // The factor picker hands its unit choice up so it can sit beside the amount: the unit
    // is part of reading a figure off a bill, not part of finding the activity. setState is
    // referentially stable, which is what the picker's contract asks for.
    const [unitChoice, setUnitChoice] = useState<IUnitChoice | null>(null);
    const [sourceMenuAnchor, setSourceMenuAnchor] = useState<HTMLElement | null>(null);
    const [confirmingConversion, setConfirmingConversion] = useState(false);
    const prevEmissionFactorSourceRef = useRef<string | undefined>(undefined);
    //Formik
    const req = (key: string, label: string) =>
      fieldFlags.mandatoryFields.includes(key)
        ? Yup.string().required(`${label} is required`)
        : Yup.string().nullable();

    const customFieldShape: Record<string, any> = {};
    (customFields ?? []).forEach(cf => {
      customFieldShape[cf.id] = fieldFlags.mandatoryFields.includes(cf.id)
        ? Yup.string().required(`${cf.fieldName} is required`)
        : Yup.string().nullable();
    });

    const transactionSchema = useMemo(() => Yup.object().shape({
      groupId: Yup.string().required(),
      subsidiaryId: emission ? Yup.string().nullable() : Yup.string().required(`Please select a ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
      countryId: emission ? Yup.string().nullable() : Yup.string().required(),
      categoryId: Yup.string().required(),
      returnId: Yup.string().required('You must provide a return'),
      measurementUnitId: Yup.string().required('You must provide units'),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      startDate: emission ? Yup.string().nullable() : Yup.string().required('Start date is required'),
      endDate: emission ? Yup.string().nullable() : Yup.string().required('End date is required'),
      customMarketBasedMultiplier: Yup.number().nullable(),
      energySupplierTariffId: Yup.string().nullable(),
      partOfEts: Yup.boolean(),
      regulatedEmissionTradingSchemeId: Yup.string().when('partOfEts', {
        is: true,
        then: schema => schema.required('You need to select a regulated emission trading scheme'),
        otherwise: schema => schema.nullable().notRequired()
      }),
      projectId: req('project', 'Project'),
      contactSupplierId: req('supplier', 'Supplier'),
      locationId: req('location', 'Location'),
      vehicleId: req('vehicle', 'Vehicle'),
      capitalGoodId: req('product', 'Product / Capital Good'),
      excludeFromReporting: Yup.boolean(),
      excludeFromReportingReason: Yup.string().when('excludeFromReporting', {
        is: true,
        then: (schema) => schema.required('You must provide a reason for exclusion'),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
      renewableEnergyCertificateIds: Yup.array().of(Yup.string()).nullable(),
      ...customFieldShape,
    }).test(
      'tariff-or-custom-xor',
      'Choose either an Energy Supplier Tariff or provide a custom Supplier Emission Rate (not both)',
      function (value) {
        const v: any = value || {}
        const hasTariff = !!v.energySupplierTariffId
        const hasCustom = v.customMarketBasedMultiplier !== null && v.customMarketBasedMultiplier !== '' && v.customMarketBasedMultiplier !== undefined
        return !(hasTariff && hasCustom)
      }
    ), [emission, screenLabelsContext, fieldFlags, customFields]);
    const formik = useFormik<any>({
      initialValues: {
        subsidiaryId: emission?.subsidiaryId ?? null,
        groupId: groupId,
        categoryId: categoryId,
        unitsConsumed: editCalculation?.unitsConsumed ?? undefined,
        energySupplierTariffId: editCalculation?.marketBasedSource === 'ENERGY_SUPPLIER_TARIFF_DETAIL_ID'
          ? editCalculation.marketBasedSourceId
          : null,
        measurementUnitId: editCalculation?.measurementUnitId ?? '',
        measurementUnitName: editCalculation?.measurementUnit?.name ?? '',
        returnId: editCalculation?.conversion?.id ?? editCalculation?.conversionId ?? '',
        conversionSourceId: editCalculation?.conversion?.conversionSourceId ?? '',
        customMarketBasedMultiplier: editCalculation?.marketBasedSource === 'CUSTOM'
          ? editCalculation.multiplier
          : undefined,
        detailLevelOne: editCalculation?.conversion?.detailLevelOne ?? '',
        detailLevelTwo: editCalculation?.conversion?.detailLevelTwo ?? '',
        detailLevelThree: editCalculation?.conversion?.detailLevelThree ?? '',
        detailLevelFour: editCalculation?.conversion?.detailLevelFour ?? '',
        detailLevelFive: editCalculation?.conversion?.detailLevelFive ?? '',
        reportPeriodId: '',
        startDate: emission?.startDate ?? '',
        endDate: emission?.endDate ?? '',
        partOfEts: !!emission?.regulatedEmissionTradingSchemeId,
        regulatedEmissionTradingSchemeId: emission?.regulatedEmissionTradingSchemeId ?? null,
        locationId: emission?.locationId ?? null,
        projectId: emission?.projectId ?? null,
        contactSupplierId: emission?.contactSupplierId ?? null,
        vehicleId: emission?.vehicleId ?? null,
        capitalGoodId: emission?.capitalGoodId ?? null,
        countryId: emission?.countryId ?? null,
        // Pre-fill with the certificates already linked to the emission being edited (aggregated
        // across its calcs by the parent); otherwise read off the calc being edited.
        renewableEnergyCertificateIds: initialRenewableEnergyCertificateIds
          ?? (editCalculation?.transactionRenewableEnergyCertificates ?? [])
            .map(link => (link as unknown as { renewableEnergyCertificateId?: string }).renewableEnergyCertificateId)
            .filter((id): id is string => Boolean(id)),
        excludeFromReporting: emission?.excludeFromReporting ?? false,
        excludeFromReportingReason: emission?.excludeFromReportingReason ?? '',
        isFlag: editCalculation?.isFlag === true,
        flagCategory: editCalculation?.flagCategory ?? '',
      },
      validationSchema: transactionSchema,
      onSubmit: (values) => {
        if (emission) {
          const transaction = {
            groupId,
            conversionSourceId: selectedEmissionFactorSourceId,
            unitsConsumed: values.unitsConsumed,
            returnId: values.returnId,
            type: 'ACTIVITY',
            customMarketBasedMultiplier: values.customMarketBasedMultiplier ?? null,
            energySupplierTariffId: values.energySupplierTariffId ?? null,
            renewableEnergyCertificateIds: hideRecSelector ? [] : (values.renewableEnergyCertificateIds ?? []),
            isFlag: values.isFlag === true,
            flagCategory: values.flagCategory || undefined,
          }
          onSubmit(transaction)
        } else {
          values.relatedConversionModelName = 'conversion'
          values.reportPeriodId = reportPeriodId || reportPeriodContext.lastSelectedReportId;
          values.dataType = 'Actual Data'
          const response = formatTransaction(values, groupId, true, customFields, categoryId)
          const form = new FormData();
          for (let i = 0; i < fileData.length; i++) {
            form.append("files", fileData[i]);
          }
          form.append('transaction', JSON.stringify(response));
          onSubmit(form)
        }
      }
    });

    //Use Effects
    useEffect(() => {
      if (userDetails && subsidiaries) {
        const selectedSubsidiary = subsidiaries.find(subsidiary => subsidiary.id === selectedSubsidiaryId)
        if (selectedSubsidiary) {
          formik.setFieldValue('subsidiaryId', selectedSubsidiary.id)
          formik.setFieldValue('countryId', selectedSubsidiary.countryId)
        }
      }
    }, [userDetails])

    useEffect(() => {
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response);
          if (emission) {
            const existingValues = (emission as IEmissionDetail).customFieldValues ?? [];
            response.forEach(cf => {
              const match = existingValues.find(v => v.customFieldId === cf.id);
              formik.setFieldValue(cf.id, match?.value ?? '');
            });
          } else {
            response.forEach(cf => { formik.setFieldValue(cf.id, ''); });
          }
        })
        .catch(err => { console.log(err); })
    }, []);

    useEffect(() => {
      if (!editCalculation) {
        formik.setFieldValue('measurementUnitId', '');
        formik.setFieldValue('returnId', '');
      }
      if (categoryId) {
        formik.setFieldValue('categoryId', categoryId);
      }
    }, [categoryId, editCalculation]);

    useEffect(() => {
      if (emission?.startDate) {
        setCalendarYear(moment(emission.startDate).format('YYYY'));
      }
    }, [emission?.startDate]);

    // Until a start date is entered, fall back to the period's own factor year so the picker
    // is usable straight away instead of waiting on the dates. reportPeriod.conversionFiguresYear
    // is the documented source of truth for which factor year applies to a period, which is the
    // right answer for a period spanning two calendar years, where neither end is obviously it.
    //
    // Browsing the wrong vintage is safe: the chosen activity is re-resolved onto the record's
    // own year, here by the resolution effect and again per calendar-year segment on save, both
    // through the same identity match. A start date always wins once there is one.
    useEffect(() => {
      if (emission?.startDate || formik.values.startDate) return;
      const period = reportPeriodContext.allReportPeriods.find(
        candidate => candidate.id === (reportPeriodId || reportPeriodContext.lastSelectedReportId),
      );
      if (period?.conversionFiguresYear) setCalendarYear(String(period.conversionFiguresYear));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emission?.startDate, formik.values.startDate, reportPeriodId,
      reportPeriodContext.allReportPeriods, reportPeriodContext.lastSelectedReportId]);

    useEffect(() => {
      if (!formik.values.returnId) {
        setConversionSelectionConfirmed(false);
        setConfirmedConversion(null);
      }
    }, [formik.values.returnId]);

    useEffect(() => {
      if (!requireConversionConfirmation) return;
      const conv = editCalculation?.conversion;
      if (conv?.id) {
        setConfirmedConversion(conv as IConversion);
        setConversionSelectionConfirmed(true);
      }
    }, [requireConversionConfirmation, editCalculation?.id, editCalculation?.conversion?.id]);

    useEffect(() => {
      const prev = prevEmissionFactorSourceRef.current;
      if (prev !== undefined && prev !== selectedEmissionFactorSourceId) {
        setConversionSelectionConfirmed(false);
        setConfirmedConversion(null);
      }
      prevEmissionFactorSourceRef.current = selectedEmissionFactorSourceId;
    }, [selectedEmissionFactorSourceId]);

    //Formik Event Handlers
    const selectEmissionFactorSourceHandler = (event: SelectChangeEvent<string>) => {
      if (event.target.value) {
        const newSelectedEmissionFactorSource = conversionSources.find(s => s.id === event.target.value)
        if (newSelectedEmissionFactorSource) {
          emissionFactorSourceHandler(newSelectedEmissionFactorSource)
        }
      }
    }
    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setValues({ ...formik.values, [name]: date.format('YYYY-MM-DD') })
      }
      if (name === 'startDate') {
        setCalendarYear(date?.format('YYYY'))
      }
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries.find(subsidiary => subsidiary.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
        setSelectedSubsidiaryId(subsidiary.id)
        const subsidiaryDefaultConversionSource = conversionSources.find(conversionSource => conversionSource.id === subsidiary.defaultConversionSourceId)
        if (subsidiaryDefaultConversionSource) {
          emissionFactorSourceHandler(subsidiaryDefaultConversionSource)
        }
      }
    }

    const handleConfirmConversionSelection = async () => {
      const id = formik.values.returnId;
      if (!id) return;
      setConfirmingConversion(true);
      try {
        const conv = await conversionService.getConversionByPk(id);
        setConfirmedConversion(conv);
        setConversionSelectionConfirmed(true);
      } catch {
        /* keep filter visible until user retries */
      } finally {
        setConfirmingConversion(false);
      }
    };

    const handleChangeConversionSelection = () => {
      setConversionSelectionConfirmed(false);
    };

    const unitLabel = formik.values.measurementUnitName?.trim?.()
      ? formik.values.measurementUnitName
      : (confirmedConversion as { measurementUnit?: { name?: string } } | null)?.measurementUnit?.name;

    // Certificate eligibility: electricity certs (REC/REGO/GoO) apply to market-based scope-2
    // Purchased Energy / Electricity; RGGO (gas) applies to scope-1 Company Facilities / Fuels
    // emissions whose conversion is natural gas. Natural gas is not its own category, so it is
    // identified by the conversion's DEFRA detail levels (read from the cascading selector's
    // formik values, which are populated for both new entry and edit).
    const conversionIsNaturalGas = formik.values.detailLevelTwo === 'Gaseous fuels'
      && ['Natural gas', 'Natural gas distributed in a pipeline'].includes(formik.values.detailLevelThree ?? '');
    const isElectricityRec = allowMarketBasedEmissionInput && groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b' && categoryId === 'df026bd9-281d-4013-80e4-a1ef92965502';
    const isGasRggo = groupId === '22cc8b45-aa77-49aa-8fb2-c39bb884fa13' && categoryId === 'f88cd55e-9e30-4605-8106-24a53e0b5f62' && conversionIsNaturalGas;
    const hideRecSelector = !(isElectricityRec || isGasRggo);
    const recTypeContext: 'electricity' | 'gas' = isGasRggo ? 'gas' : 'electricity';

    // Collected under Advanced rather than above the fold: defaulted, rarely changed, and a
    // property of the organisation rather than the record being written. It still shows as a
    // caption under the factor field, because which source resolved a factor is a disclosure,
    // not an implementation detail.
    const conversionSourceField = (
      <>
        {conversionSources &&
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }} >
            <InputLabel htmlFor='conversionSourceId'>Conversion Factor Source</InputLabel>
            <Select id='conversionSourceId' name='conversionSourceId' label='Conversion Factor Source'
              value={selectedEmissionFactorSourceId || ''}
              onChange={selectEmissionFactorSourceHandler}>
              {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) => (
                <MenuItem key={index} value={conversionSource.id}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <span>{conversionSource.source}</span>
                    {conversionSource.country && (
                      <img
                        loading="lazy"
                        width="20"
                        src={conversionSource.country.imageUrl}
                        alt={`${conversionSource.country.name} flag`}
                      />
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>}
      </>
    )

    const selectedSourceName = conversionSources?.find(source => source.id === selectedEmissionFactorSourceId)?.source

    // Which source resolved the factor sits beside the factor it qualifies, and doubles as the
    // way to change it: the full control still lives under Advanced, but reaching for it from
    // here is what you want when the factor in front of you came from the wrong dataset.
    const factorSourceShortcut = selectedSourceName ? (
      <>
        <Link
          component='button'
          type='button'
          variant='caption'
          underline='hover'
          onClick={(event) => setSourceMenuAnchor(event.currentTarget)}
          sx={{ color: 'text.secondary', cursor: 'pointer', textAlign: 'right', flexShrink: 1, minWidth: 0 }}
        >
          Factors from {selectedSourceName}
        </Link>
        <Menu
          anchorEl={sourceMenuAnchor}
          open={Boolean(sourceMenuAnchor)}
          onClose={() => setSourceMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {(conversionSources ?? []).slice().sort((a, b) => a.source.localeCompare(b.source)).map(conversionSource => (
            <MenuItem
              key={conversionSource.id}
              selected={conversionSource.id === selectedEmissionFactorSourceId}
              onClick={() => {
                setSourceMenuAnchor(null);
                if (conversionSource.id !== selectedEmissionFactorSourceId) {
                  emissionFactorSourceHandler(conversionSource);
                }
              }}
            >
              <Stack direction='row' spacing={1} alignItems='center'>
                <span>{conversionSource.source}</span>
                {conversionSource.country && (
                  <img
                    loading='lazy'
                    width='20'
                    src={conversionSource.country.imageUrl}
                    alt={`${conversionSource.country.name} flag`}
                  />
                )}
              </Stack>
            </MenuItem>
          ))}
        </Menu>
      </>
    ) : null

    return (
      <Box sx={{ mt: 2 }}>
        <Box component='form' onSubmit={formik.handleSubmit}>
          <Box sx={{ mr: 2 }}>
            {!emission && (
              <Stack direction='column' spacing={2} sx={{ mt: 2 }}>
                {/* First on the form: which entity the record belongs to frames everything after
                    it, and it is the one field here that cannot be corrected later without
                    re-cutting the consolidation boundary. */}
              {!emission && subsidiaries && (userDetails?.organisation?.enableSubsidiaries || subsidiaries.length > 1) &&
                <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
                  <InputLabel htmlFor='subsidiaryId'> {screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })}</InputLabel>
                  <Select
                    id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })} name='subsidiaryId'
                    value={formik.values.subsidiaryId || ''}
                    onChange={subsidiaryHandler}
                    error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                    sx={{ color: 'text.pprimary', }}>
                    {getSelectableSubsidiaries(subsidiaries).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                      <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
              }
                <Stack direction='row' spacing={2}>
                  <FormControl fullWidth variant='outlined'>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        minDate={moment(reportPeriodContext.getAllowedInputDates()?.startDate)}
                        maxDate={formik.values.endDate ? moment(formik.values.endDate) : moment(reportPeriodContext.getAllowedInputDates()?.endDate)}
                        label='Start Date'
                        value={formik.values.startDate ? moment(formik.values.startDate) : null}
                        onChange={(date) => dateHandler(date, 'startDate')}
                        format='DD/MM/YYYY'
                        sx={{ color: 'text.primary' }}
                        open={openStartDate}
                        onOpen={() => setOpenStartDate(true)}
                        onClose={() => setOpenStartDate(false)}
                        slotProps={{
                          textField: {
                            onClick: () => setOpenStartDate(true),
                            error: formik.touched.startDate && Boolean(formik.errors.startDate)
                          },
                        }}
                      />
                    </LocalizationProvider>
                    {formik.touched.startDate && formik.errors.startDate &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                        {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
                      </FormHelperText>
                    }
                  </FormControl>
                  <FormControl fullWidth variant='outlined'>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        minDate={moment(formik.values.startDate)}
                        maxDate={moment(reportPeriodContext.getAllowedInputDates()?.endDate)}
                        label='End Date'
                        value={formik.values.endDate ? moment(formik.values.endDate) : null}
                        onChange={(date) => dateHandler(date, 'endDate')}
                        format='DD/MM/YYYY'
                        sx={{ color: 'text.primary' }}
                        open={openEndDate}
                        onOpen={() => setOpenEndDate(true)}
                        onClose={() => setOpenEndDate(false)}
                        slotProps={{
                          textField: {
                            onClick: () => setOpenEndDate(true),
                            error: formik.touched.endDate && Boolean(formik.errors.endDate)
                          },
                        }}
                      />
                    </LocalizationProvider>
                    {formik.touched.endDate && formik.errors.endDate &&
                      <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                        {formik.errors.endDate ? <div>{formik.errors.endDate as string}</div> : null}
                      </FormHelperText>
                    }
                  </FormControl>
                </Stack>
              </Stack>
            )}
            {calendarYear && categoryId && groupId && selectedEmissionFactorSourceId ? (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {requireConversionConfirmation ? (
                  !conversionSelectionConfirmed ? (
                    <>
                      <EmissionsFilterLogic
                        categoryId={categoryId}
                        groupId={groupId}
                        calendarYear={calendarYear}
                        defaultConversionSourceId={selectedEmissionFactorSourceId}
                        formik={formik}
                        isEdit={!!editCalculation}
                        direction='row'
                        externalUnitSelect
                        onUnitChoiceChange={setUnitChoice}
                        browseRowTrailing={factorSourceShortcut}
                      />
                      {formik.values.returnId ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                          <Button
                            variant='contained'
                            color='primary'
                            disabled={confirmingConversion}
                            onClick={handleConfirmConversionSelection}
                          >
                            {confirmingConversion ? 'Loading…' : 'Select'}
                          </Button>
                          <Typography variant='caption' color='text.secondary'>
                            Confirm this conversion factor before entering the amount below.
                          </Typography>
                        </Stack>
                      ) : null}
                    </>
                  ) : confirmedConversion ? (
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50',
                      }}
                    >
                      <Typography variant='overline' color='primary' sx={{ letterSpacing: 0.08, fontWeight: 600 }}>
                        Selected conversion factor
                      </Typography>
                      <Typography variant='subtitle1' sx={{ mt: 1, mb: 0.5, fontWeight: 600, lineHeight: 1.4 }}>
                        {humanConversionPath(confirmedConversion)}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        {conversionSources.find(s => s.id === (confirmedConversion.conversionSourceId ?? selectedEmissionFactorSourceId))?.source ?? 'Conversion source'}{' '}
                        · Year {confirmedConversion.calendarYear ?? calendarYear}
                        {unitLabel ? ` · per ${unitLabel}` : ''}
                      </Typography>
                      <Typography variant='body2' sx={{ mb: 2 }}>
                        <Typography component='span' color='text.secondary'>Emission multiplier (kg CO₂e per activity unit){' '}</Typography>
                        <Typography component='span' fontWeight={700} sx={{ ml: 0.5 }}>
                          {formatEmissionMultiplier(confirmedConversion.multiplier)}
                        </Typography>
                      </Typography>
                      <Link
                        component='button'
                        type='button'
                        variant='body2'
                        underline='hover'
                        onClick={handleChangeConversionSelection}
                        sx={{ cursor: 'pointer' }}
                      >
                        Change conversion factor
                      </Link>
                    </Paper>
                  ) : null
                ) : (
                  <EmissionsFilterLogic
                    categoryId={categoryId}
                    groupId={groupId}
                    calendarYear={calendarYear}
                    defaultConversionSourceId={selectedEmissionFactorSourceId}
                    formik={formik}
                    isEdit={!!editCalculation}
                    direction='row'
                    externalUnitSelect
                    onUnitChoiceChange={setUnitChoice}
                    browseRowTrailing={factorSourceShortcut}
                  />
                )}
              </Stack>
            ) : (
              /* The real search cannot mount until it knows what to search, so a disabled twin
                 holds its place. The form keeps its shape instead of growing a field when the
                 dates are filled in, and the reason sits in the field rather than in a sentence
                 next to it. */
              <TextField
                fullWidth
                disabled
                value=''
                label='Emission factor'
                placeholder={!calendarYear
                  ? 'Choose the dates above first'
                  : 'Choose a conversion factor source under Advanced first'}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            )}
            <Grid>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, mb: 2 }}>
                {/* 4:1 of the row, so Amount takes 80% and the unit the remaining fifth.
                    Stacked on a narrow screen both go full width. */}
                <FormControl fullWidth variant='outlined' sx={{ flex: { sm: 4 } }}>
                  <InputLabel htmlFor='unitsConsumed'>Amount</InputLabel>
                  <OutlinedInput inputProps={{ min: 0.01, step: 0.01 }} id='unitsConsumed' label='amount' name='unitsConsumed' type='number'
                    value={formik.values.unitsConsumed} onChange={formik.handleChange}
                    error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.unitsConsumed ? <div>{formik.errors.unitsConsumed as string}</div> : null}
                    </FormHelperText>
                  }
                </FormControl>
                {/* Always present, so the row does not reflow when a factor is chosen: the
                    factor picker fills its options in and enables it once it has units. */}
                <FormControl fullWidth variant='outlined' sx={{ flex: { sm: 1 } }} disabled={!unitChoice || unitChoice.disabled}>
                  <InputLabel htmlFor='measurementUnitName'>Unit</InputLabel>
                  <Select
                    id='measurementUnitName'
                    label='Unit'
                    value={unitChoice?.value ?? ''}
                    onOpen={unitChoice?.onOpen}
                    onChange={(event) => unitChoice?.onChange(event.target.value as string)}
                    error={formik.touched.measurementUnitId && Boolean(formik.errors.measurementUnitId)}
                    sx={{ color: 'text.primary' }}>
                    {(unitChoice?.options ?? []).map(unit => (
                      <MenuItem key={unit.id} value={unit.name}>{unit.name}</MenuItem>
                    ))}
                  </Select>
                  {formik.touched.measurementUnitId && formik.errors.measurementUnitId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.measurementUnitId as string}
                    </FormHelperText>
                  }
                </FormControl>
              </Stack>
            </Grid>
            {/* Rendered whether or not the custom fields have loaded. This block now carries the
                subsidiary and the conversion factor source, so gating it on that request would
                mean a failed or slow lookup of unrelated fields could take the source control
                off the form entirely. */}
            {(
              emission ? (
                <EmissionsInputAdvanced
                  customFields={fieldFlags.visibleCustomFields}
                  formik={formik}
                  isEdit={true}
                  hideCountry={fieldFlags.hideCountry}
                  hideLocation={fieldFlags.hideLocation}
                  hideSupplier={fieldFlags.hideSupplier}
                  hideVehicle={fieldFlags.hideVehicle}
                  hideProduct={fieldFlags.hideProduct}
                  hideProject={fieldFlags.hideProject}
                  hideRec={hideRecSelector}
                  recTypeContext={recTypeContext}
                  hideCustomFields={fieldFlags.visibleCustomFields.length === 0}
                  hideRegulatedEmissionTradingScheme={fieldFlags.hideEts || !isEtsGroupId(groupId)}
                  hideExcludedFromReporting={fieldFlags.hideExcludeFromReporting}
                  hideEnergySupplierTariff={!(allowMarketBasedEmissionInput && groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b' && categoryId === 'df026bd9-281d-4013-80e4-a1ef92965502')}
                  hideCustomBasedMarketMultiplier={!(allowMarketBasedEmissionInput && groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b' && categoryId === 'df026bd9-281d-4013-80e4-a1ef92965502')}
                  mandatoryFields={fieldFlags.mandatoryFields}
                  leadingFields={conversionSourceField}
                />
              ) : (
                <EmissionsInputAdvanced
                  customFields={fieldFlags.visibleCustomFields}
                  formik={formik}
                  hideCountry={fieldFlags.hideCountry}
                  hideLocation={fieldFlags.hideLocation}
                  hideSupplier={fieldFlags.hideSupplier}
                  hideVehicle={fieldFlags.hideVehicle}
                  hideProduct={fieldFlags.hideProduct}
                  hideProject={fieldFlags.hideProject}
                  hideRegulatedEmissionTradingScheme={fieldFlags.hideEts || !isEtsGroupId(groupId)}
                  hideExcludedFromReporting={fieldFlags.hideExcludeFromReporting}
                  hideRec={hideRecSelector}
                  recTypeContext={recTypeContext}
                  hideCustomBasedMarketMultiplier={!(allowMarketBasedEmissionInput && groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b' && categoryId === 'df026bd9-281d-4013-80e4-a1ef92965502')}
                  hideEnergySupplierTariff={!(allowMarketBasedEmissionInput && groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b' && categoryId === 'df026bd9-281d-4013-80e4-a1ef92965502')}
                  mandatoryFields={fieldFlags.mandatoryFields}
                  leadingFields={conversionSourceField}
                />
              )
            )}
            <Grid>
              <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
                {!emission && <FileUploadHelper fileData={fileData} setFileData={setFileData} disableButton={isSubmitting} />}
                <DialogSaveButtons
                  isForm={true}
                  isSubmitting={isSubmitting}
                  cancelHandler={cancelHandler}
                  hideCancelButton={!cancelHandler}
                  disableSaveButton={
                    !formik.values.returnId
                    || (requireConversionConfirmation && !conversionSelectionConfirmed)
                  }
                  disabledReason='Finish choosing an emission factor above to save this record'
                  submitHandler={formik.submitForm}
                />
              </Stack>
            </Grid>
          </Box>
        </Box>
      </Box >
    )
  }